import React, { useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { components } from 'react-select';
import { CheckboxField } from '../formFields/CheckboxField';
import { KText } from 'ui-library/components';

/**
 * MultiCheckDropdownMenu Component
 *
 * This component creates a nested multi-select dropdown with checkboxes using React, Chakra UI, and react-select.
 *
 * Key Features:
 * - Supports hierarchical options with parent-child relationships (determined by `parent_id`).
 * - Props:
 *   - `isMulti`: Allows multi-selection when true.
 *   - `includeParentInValue`: Includes the parent in the selection if true.
 * - Dynamic depth rendering for nested options.
 * - Checkbox states: Selected, Indeterminate (partial selection), and Unselected.
 *
 * Utility Functions:
 * - `getChildOptions`: Fetches direct children of a given option.
 * - `getAllDescendants`: Fetches all nested children of a given option.
 * - `getRootOptions`: Fetches top-level options without a `parent_id`.
 *
 * Ideal for use cases like category/tag selection, hierarchical filters, or nested data management.
 */

const getChildOptions = (options, parentValue) => {
  // Require a non-nullish parent_id so options without one are never treated
  // as children of a parent whose value is also nullish (would self-recurse).
  return options.filter(
    (option) =>
      option.parent_id !== undefined &&
      option.parent_id !== null &&
      option.parent_id === parentValue
  );
};

const getAllDescendants = (options, parentValue) => {
  let descendants = [];
  const children = getChildOptions(options, parentValue);

  children.forEach((child) => {
    descendants.push(child);
    descendants = [...descendants, ...getAllDescendants(options, child.value)];
  });

  return descendants;
};

const getRootOptions = (options) => {
  return options.filter(
    (option) => option.parent_id === undefined || option.parent_id === null
  );
};

const NestedCheckOption = ({
  option,
  allOptions,
  selectedValues,
  onChange,
  depth = 0,
  valueKey = 'value',
  isMulti,
  includeParentInValue = true,
}) => {
  const children = getChildOptions(allOptions, option[valueKey]);
  const checkboxRef = React.useRef();

  const isSelected = selectedValues?.some(
    (val) => val[valueKey] === option[valueKey]
  );

  const descendants = getAllDescendants(allOptions, option[valueKey]);
  const allChildrenSelected =
    descendants.length > 0 &&
    descendants.every((desc) =>
      selectedValues?.some((val) => val[valueKey] === desc[valueKey])
    );

  const someChildrenSelected =
    descendants.length > 0 &&
    descendants.some((desc) =>
      selectedValues?.some((val) => val[valueKey] === desc[valueKey])
    ) &&
    !allChildrenSelected;

  const isIndeterminate = someChildrenSelected;

  const handleSelect = (e) => {
    e.preventDefault();
    e.stopPropagation();

    let newValue = [...(selectedValues || [])];

    if (isMulti) {
      if (children.length > 0) {
        // This is a parent/group option
        if (isSelected || allChildrenSelected) {
          // Deselect this option and all its descendants
          newValue = newValue.filter(
            (val) =>
              val[valueKey] !== option[valueKey] &&
              !descendants.some((desc) => desc[valueKey] === val[valueKey])
          );
        } else {
          // Select all descendants and optionally the parent
          const valuesToAdd = [...descendants];
          if (includeParentInValue) {
            valuesToAdd.unshift(option);
          }
          newValue = [
            ...newValue,
            ...valuesToAdd.filter(
              (opt) => !newValue.some((val) => val[valueKey] === opt[valueKey])
            ),
          ];
        }
      } else {
        // This is an individual option (leaf node)
        if (isSelected) {
          // Deselect this option
          newValue = newValue.filter(
            (val) => val[valueKey] !== option[valueKey]
          );
        } else {
          // Select this option
          newValue = [...newValue, option];
        }
      }
    } else {
      newValue = [option];
    }

    onChange(isMulti ? newValue : newValue[0], {
      action: isSelected ? 'deselect-option' : 'select-option',
      option: option,
    });
  };

  return (
    <Box id={`react-select-nested-option-container-${option[valueKey]}`}>
      <Flex
        id={`react-select-nested-option-flex-${option[valueKey]}`}
        pl={`${depth * 24}px`}
        py="2"
        alignItems="center"
        cursor="pointer"
        _hover={{ bg: 'gray.50' }}
        onClick={handleSelect}
      >
        <CheckboxField
          item={{
            name: `react-select-nested-option-checkbox-${option[valueKey]}`,
          }}
          field={{
            ref: checkboxRef,
            value: isSelected || allChildrenSelected,
            isIndeterminate,
            mr: '2',
            onChange: () => {},
            onClick: (e) => {
              e.stopPropagation();
              handleSelect(e);
            },
          }}
        />

        <KText
          id={`react-select-nested-option-text-${option[valueKey]}`}
          size={depth === 0 ? 'sm-semi-bold' : 'sm-medium'}
        >
          {option.label}
        </KText>
      </Flex>
      {children.map((child) => (
        <NestedCheckOption
          key={child[valueKey]}
          option={child}
          allOptions={allOptions}
          selectedValues={selectedValues}
          onChange={onChange}
          depth={depth + 1}
          valueKey={valueKey}
          isMulti={isMulti}
          includeParentInValue={includeParentInValue}
        />
      ))}
    </Box>
  );
};

export const MultiCheckDropdownMenu = (props) => {
  const { options = [], selectProps = {} } = props;
  const {
    value,
    onChange,
    isMulti = true,
    extraProps = {},
    onMenuClose,
    menuIsOpen,
  } = selectProps;

  useEffect(() => {
    const handleClickOutside = (event) => {
      const selectContainer = event.target.closest(
        '.kula_custom_select__container'
      );
      const menuContainer = event.target.closest('.kula_custom_select__menu');

      if (menuIsOpen && !selectContainer && !menuContainer) {
        onMenuClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuIsOpen, onMenuClose]);

  const { valueKey = 'value', includeParentInValue = true } = extraProps;

  const handleChange = (newValue, actionMeta) => {
    const finalValue = isMulti ? newValue : [newValue];
    onChange(isMulti ? finalValue : finalValue[0], actionMeta);
    onMenuClose();
  };

  const rootOptions = getRootOptions(options);

  return (
    <components.Menu {...props}>
      <Box
        id="react-select-multi-check-dropdown-container"
        maxH="300px"
        overflowY="auto"
      >
        {rootOptions.map((option) => (
          <NestedCheckOption
            key={option[valueKey]}
            option={option}
            allOptions={options}
            selectedValues={Array.isArray(value) ? value : value ? [value] : []}
            onChange={handleChange}
            valueKey={valueKey}
            isMulti={isMulti}
            includeParentInValue={includeParentInValue}
          />
        ))}
      </Box>
    </components.Menu>
  );
};
